'use client'

import * as React from 'react'
import { CheckCircle, AlertTriangle, Pause } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card'
import { Badge } from 'components/ui/badge'
import { Progress } from 'components/ui/progress'
import { cn } from 'lib/utils'
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, BarChart, Bar } from 'recharts'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '../../../../firebase'

const statusIcons = {
  active: <CheckCircle className="size-4 text-success" />,
  'at-risk': <AlertTriangle className="size-4 text-warning" />,
  completed: <CheckCircle className="size-4 text-primary" />,
  'on-hold': <Pause className="size-4 text-muted-foreground" />,
  planning: <Pause className="size-4 text-muted-foreground" />,
}

const parseAmount = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const sanitized = value.replace(/[^0-9.-]+/g, '')
    const parsed = Number(sanitized)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

const formatCompactKES = (value) => {
  const amount = parseAmount(value)
  if (amount >= 1_000_000_000) return `KES ${(amount / 1_000_000_000).toFixed(1)}B`
  if (amount >= 1_000_000) return `KES ${(amount / 1_000_000).toFixed(1)}M`
  if (amount >= 1_000) return `KES ${(amount / 1_000).toFixed(1)}K`
  return `KES ${Math.round(amount)}`
}

const sumBudgetItems = (items = []) =>
  items.reduce((sum, item) => {
    const total = parseAmount(item.total)
    if (total > 0) return sum + total
    return sum + parseAmount(item.unitCost) * parseAmount(item.quantity)
  }, 0)

const normalizeStatus = (value) => String(value || '').toLowerCase().replace(/_/g, '-')

const toDateValue = (value) => {
  if (!value) return null
  if (typeof value?.toDate === 'function') return value.toDate()
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1)
const addMonths = (date, count) => new Date(date.getFullYear(), date.getMonth() + count, 1)
const formatMonthLabel = (date) => date.toLocaleDateString('en-KE', { month: 'short', year: '2-digit' })

const getPlannedProgress = (project, asOfDate) => {
  const start = toDateValue(project.startDate)
  const end = toDateValue(project.endDate)
  if (!start || !end) return 0
  if (asOfDate <= start) return 0
  if (asOfDate >= end) return 100
  const total = end.getTime() - start.getTime()
  const elapsed = asOfDate.getTime() - start.getTime()
  return Math.max(0, Math.min(100, Math.round((elapsed / total) * 100)))
}

const getActualProgress = (project, deliverables = []) => {
  if (deliverables.length > 0) {
    const total = deliverables.length
    const completed = deliverables.filter((task) => String(task.status || '').toLowerCase() === 'completed').length
    return Math.round((completed / total) * 100)
  }
  return parseAmount(project.progress || project.percentComplete || 0)
}

export default function DeliveryDashboard() {
  const [projects, setProjects] = React.useState([])
  const [deliverables, setDeliverables] = React.useState([])
  const [budgetCategories, setBudgetCategories] = React.useState([])
  const [expenditures, setExpenditures] = React.useState([])
  const [reports, setReports] = React.useState([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    let isMounted = true

    async function loadDelivery() {
      try {
        const [projectSnap, deliverableSnap, categorySnap, expenseSnap, reportSnap] = await Promise.all([
          getDocs(collection(db, 'projects')),
          getDocs(collection(db, 'deliverables')),
          getDocs(collection(db, 'budgetCategories')),
          getDocs(collection(db, 'expenditures')),
          getDocs(collection(db, 'reports')),
        ])
        if (!isMounted) return
        setProjects(projectSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })))
        setDeliverables(deliverableSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })))
        setBudgetCategories(categorySnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })))
        setExpenditures(expenseSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })))
        setReports(reportSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })))
      } catch {
        if (!isMounted) return
        setProjects([])
        setDeliverables([])
        setBudgetCategories([])
        setExpenditures([])
        setReports([])
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadDelivery()

    return () => {
      isMounted = false
    }
  }, [])

  const deliverablesByProject = deliverables.reduce((acc, task) => {
    if (!task.projectId) return acc
    if (!acc[task.projectId]) acc[task.projectId] = []
    acc[task.projectId].push(task)
    return acc
  }, {})

  const budgetByProject = budgetCategories.reduce((acc, item) => {
    if (!item.projectId) return acc
    acc[item.projectId] = (acc[item.projectId] || 0) + parseAmount(item.approved || item.total)
    return acc
  }, {})

  const expenditureByProject = expenditures.reduce((acc, exp) => {
    if (!exp.projectId) return acc
    acc[exp.projectId] = (acc[exp.projectId] || 0) + parseAmount(exp.amount || 0)
    return acc
  }, {})

  const projectSummaries = projects.map((project) => {
    const status = normalizeStatus(project.status)
    const tasks = deliverablesByProject[project.id] || []
    const planned = getPlannedProgress(project, new Date())
    const actual = getActualProgress(project, tasks)
    const budgeted =
      budgetByProject[project.id] ||
      parseAmount(project.totalBudget || 0) ||
      (Array.isArray(project.budgetItems) ? sumBudgetItems(project.budgetItems) : 0)
    const spent = expenditureByProject[project.id] || 0
    const reportsForProject = reports.filter((report) => report.projectId === project.id)
    const overdue = reportsForProject.filter((report) => String(report.status || '').toLowerCase() === 'overdue').length
    return {
      ...project,
      status,
      planned,
      actual,
      budgeted,
      spent,
      overdue,
    }
  })

  const totalProjects = projectSummaries.length
  const activeProjects = projectSummaries.filter((project) => project.status === 'active').length
  const atRiskProjects = projectSummaries.filter((project) => project.status === 'at-risk').length
  const completedProjects = projectSummaries.filter((project) => project.status === 'completed').length
  const avgProgress = totalProjects > 0 ? Math.round(projectSummaries.reduce((sum, project) => sum + project.actual, 0) / totalProjects) : 0

  const sCurveMonths = Array.from({ length: 6 }, (_, index) => startOfMonth(addMonths(new Date(), index - 5)))
  const sCurveData = sCurveMonths.map((month) => {
    const planned = projectSummaries.length
      ? Math.round(projectSummaries.reduce((sum, project) => sum + getPlannedProgress(project, month), 0) / projectSummaries.length)
      : 0
    const actual = projectSummaries.length
      ? Math.round(projectSummaries.reduce((sum, project) => sum + project.actual, 0) / projectSummaries.length)
      : 0
    return {
      month: formatMonthLabel(month),
      planned,
      actual,
    }
  })

  const budgetChartData = projectSummaries.map((project) => ({
    name: project.code || project.shortCode || project.title || 'Project',
    budgeted: project.budgeted,
    actual: project.spent,
  }))

  return (
    <div className="flex flex-col gap-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">Delivery Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Overall programme delivery performance, timelines, and milestones</p>
      </div>

      {/* Status Summary */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-foreground">{loading ? '—' : totalProjects}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Total Projects</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-success">{loading ? '—' : activeProjects}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Active</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-success" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-warning">{loading ? '—' : atRiskProjects}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">At Risk</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-warning" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-primary">{loading ? '—' : completedProjects}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Completed</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-foreground">{loading ? '—' : `${avgProgress}%`}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Avg Progress</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-muted-foreground/30" />
          </CardContent>
        </Card>
      </div>

      {/* S-Curve + Budget */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-foreground">S-Curve: Planned vs Actual Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={sCurveData}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.9 0.01 145)" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                <YAxis tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" unit="%" />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Area type="monotone" dataKey="planned" stroke="oklch(0.35 0.12 145)" fill="oklch(0.35 0.12 145)" fillOpacity={0.1} name="Planned" strokeWidth={2} />
                <Area type="monotone" dataKey="actual" stroke="oklch(0.87 0.15 85)" fill="oklch(0.87 0.15 85)" fillOpacity={0.2} name="Actual" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-foreground">Budget Utilisation by Project</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={budgetChartData} barSize={14}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.9 0.01 145)" />
                <XAxis dataKey="name" tick={{ fontSize: 9 }} stroke="oklch(0.48 0.03 145)" />
                <YAxis tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" tickFormatter={(v) => formatCompactKES(v)} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} formatter={(v) => formatCompactKES(v)} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="budgeted" fill="oklch(0.35 0.12 145)" name="Budgeted" radius={[3, 3, 0, 0]} />
                <Bar dataKey="actual" fill="oklch(0.55 0.16 150)" name="Spent" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Project Health Cards */}
      <Card className="border border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-foreground">Project Health Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3">
            {loading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">Loading projects...</div>
            ) : projectSummaries.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">No projects found.</div>
            ) : (
              projectSummaries.map((project) => (
                <div key={project.id} className="flex items-center gap-4 rounded-lg border border-border p-3">
                  {statusIcons[project.status]}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-foreground truncate">{project.title || 'Project'}</span>
                      <Badge variant="outline" className="text-[10px] shrink-0 text-foreground">
                        {project.code || project.shortCode || 'PRJ'}
                      </Badge>
                    </div>
                    <p className="text-[10px] text-muted-foreground">PI: {project.principalInvestigatorName || 'PI'}</p>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="flex items-center gap-2 min-w-[120px]">
                      <Progress value={project.actual} className="h-2 flex-1" />
                      <span className="text-[10px] font-semibold text-foreground">{project.actual}%</span>
                    </div>
                    <Badge
                      className={cn(
                        'text-[10px] capitalize',
                        project.status === 'active'
                          ? 'bg-success/15 text-success'
                          : project.status === 'at-risk'
                          ? 'bg-warning/15 text-warning'
                          : project.status === 'completed'
                          ? 'bg-primary/15 text-primary'
                          : 'bg-muted text-muted-foreground',
                      )}
                    >
                      {project.status}
                    </Badge>
                    {project.overdue > 0 && (
                      <Badge className="bg-destructive/15 text-destructive text-[10px]">{project.overdue} overdue</Badge>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
